/**
 * useFilterPersistence.js
 *
 * A reusable hook for persistent, URL-driven filter state.
 *
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │  Source of truth priority on mount:                                      │
 * │    1. URL query params  (canonical — enables shareable/bookmarkable URLs) │
 * │    2. localStorage      (fallback — survives page refresh without URL)    │
 * │    3. defaultFilters    (hard-coded application defaults)                 │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * Sync on filter change:
 *   - Local React state updates immediately (responsive UI).
 *   - URL and localStorage are synced after a debounce delay.
 *     • Text inputs: debounced (default 300 ms) — avoids excessive history entries.
 *     • Other inputs: synced on the next tick (effectively immediate).
 *
 * Browser navigation:
 *   - Filter updates use `replaceState` (no new history entry) via react-router
 *     `setSearchParams(..., { replace: true })`.
 *   - Back/forward button presses fire `popstate`, which the hook listens to so
 *     it can restore the filter state for the navigated URL.
 *   - `replaceState` does NOT fire `popstate`, so there are no sync loops.
 *
 * Anti-patterns avoided:
 *   - No double-render on mount: state is lazily initialised from URL/localStorage.
 *   - No stale-closure bugs: latest state is always read from refs inside timeouts.
 *   - No infinite URL-update loops: URL writes use replace (no popstate) and the
 *     local state is not derived from `searchParams` after mount.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * API
 * ─────────────────────────────────────────────────────────────────────────────
 * const {
 *   // Filter array
 *   filters,           Array of { type: number, filterValue: string }
 *   setFilter,         (type, value) => void  — non-text; immediate URL sync
 *   setFilterText,     (type, value) => void  — text input; debounced URL sync
 *   setFilters,        (filtersArray) => void — replace all at once
 *   resetFilters,      ()            => void  — clear everything
 *   getFilterValue,    (type)        => string
 *   hasActiveFilters,  boolean
 *
 *   // Extra params (toggles, date ranges — any non-filter-array URL state)
 *   extraParams,       { [key]: value }
 *   setExtraParam,     (key, value) => void
 *
 *   // Pagination
 *   page,              number (0-based)
 *   setPage,           (page) => void
 *   pageSize,          number
 *   setPageSize,       (size) => void
 * } = useFilterPersistence({
 *   pageKey:            'graphs-overview',     // unique; used for localStorage key
 *   filterSchema:       { general: 0 },        // { urlParamName: filterType }
 *   defaultFilters:     [],
 *   extraParamDefaults: { toggle: 1 },         // default values for extra params
 *   defaultPage:        0,
 *   defaultPageSize:    25,
 *   textDebounceMs:     300,
 *   persistPageSize:    true,
 * });
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * filterSchema maps URL-friendly string keys to the numeric filter type constants
 * defined in Config/constants.js.
 *
 * Example:
 *   filterSchema: { general: GraphFilterTypes.GENERAL }
 *   ↓ active filter: { type: 0, filterValue: 'test' }
 *   ↓ URL: ?general=test
 *
 * extraParamDefaults keys are serialised into the URL with an "x_" prefix to
 * avoid clashing with filterSchema keys or pagination params.
 *
 * Example:
 *   extraParamDefaults: { toggle: 1, startDate: '', endDate: '' }
 *   ↓ URL: ?x_toggle=2&x_startDate=2024-01-01&x_endDate=2024-12-31
 *   (default values are omitted from the URL to keep it clean)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { saveFilterState, loadFilterState, clearFilterState } from '../Utils/filterStorage';

// ─── Pure helpers (no React — safe to call anywhere) ──────────────────────────

/**
 * Build a filter array from URLSearchParams using the provided schema.
 * Filters with empty values are excluded.
 *
 * @param {URLSearchParams} params
 * @param {Object}          schema  { urlKey: filterType }
 * @returns {Array}  [{ type, filterValue }, ...]
 */
function parseFiltersFromParams(params, schema) {
    const result = [];
    for (const [urlKey, filterType] of Object.entries(schema)) {
        const value = params.get(urlKey);
        if (value !== null && value !== '') {
            result.push({ type: filterType, filterValue: value });
        }
    }
    return result;
}

/**
 * Build an extra-params object from URLSearchParams.
 * Coerces values to match the type of their default.
 *
 * @param {URLSearchParams} params
 * @param {Object}          defaults  { key: defaultValue }
 * @returns {Object}
 */
function parseExtraParamsFromParams(params, defaults) {
    const result = {};
    for (const [key, defaultValue] of Object.entries(defaults)) {
        const raw = params.get('x_' + key);
        if (raw !== null) {
            if (typeof defaultValue === 'number') {
                const parsed = Number(raw);
                result[key] = isNaN(parsed) ? defaultValue : parsed;
            } else if (typeof defaultValue === 'boolean') {
                result[key] = raw === 'true';
            } else {
                result[key] = raw;
            }
        } else {
            result[key] = defaultValue;
        }
    }
    return result;
}

/**
 * Parse page index from URLSearchParams. Returns null if absent or invalid.
 * @param {URLSearchParams} params
 * @returns {number|null}
 */
function parsePageFromParams(params) {
    const raw = params.get('page');
    if (raw === null) return null;
    const parsed = parseInt(raw, 10);
    return isNaN(parsed) || parsed < 0 ? null : parsed;
}

/**
 * Parse page size from URLSearchParams. Returns null if absent or invalid.
 * @param {URLSearchParams} params
 * @returns {number|null}
 */
function parsePageSizeFromParams(params) {
    const raw = params.get('pageSize');
    if (raw === null) return null;
    const parsed = parseInt(raw, 10);
    return isNaN(parsed) || parsed < 1 ? null : parsed;
}

/**
 * Serialise filter state into a URLSearchParams instance.
 * Empty/default values are omitted to keep URLs minimal.
 *
 * @param {Array}  filters
 * @param {Object} schema           { urlKey: filterType }
 * @param {Object} extras           { key: value }
 * @param {Object} extraDefaults    { key: defaultValue }
 * @param {number} page
 * @param {number} pageSize
 * @param {number} defaultPage
 * @param {number} defaultPageSize
 * @param {boolean} persistPageSize
 * @returns {URLSearchParams}
 */
function buildParams(filters, schema, extras, extraDefaults, page, pageSize, defaultPage, defaultPageSize, persistPageSize) {
    const params = new URLSearchParams();

    // --- Filter array ---------------------------------------------------------
    for (const { type, filterValue } of filters) {
        if (filterValue === null || filterValue === '' || filterValue === undefined) continue;
        const urlKey = Object.keys(schema).find(k => schema[k] === type);
        if (urlKey) params.set(urlKey, String(filterValue));
    }

    // --- Extra params ---------------------------------------------------------
    for (const [key, value] of Object.entries(extras)) {
        const defaultVal = extraDefaults[key];
        if (value === null || value === '' || value === undefined) continue;
        if (String(value) === String(defaultVal)) continue; // omit default values
        params.set('x_' + key, String(value));
    }

    // --- Pagination -----------------------------------------------------------
    // Omit default page (0) to keep URLs clean
    if (page > defaultPage) {
        params.set('page', String(page));
    }
    // Omit default page size to keep URLs clean
    if (persistPageSize && pageSize !== defaultPageSize) {
        params.set('pageSize', String(pageSize));
    }

    return params;
}

/**
 * Immutably upsert a single filter in a filter array.
 * - If value is empty/null: removes the filter for that type.
 * - If a filter with the type exists: replaces it.
 * - Otherwise: prepends a new filter.
 *
 * @param {Array}  filters
 * @param {number} type
 * @param {*}      value
 * @returns {Array}
 */
function upsertFilter(filters, type, value) {
    const withoutType = filters.filter(x => x.type !== type);
    if (value === '' || value === null || value === undefined) {
        return withoutType;
    }
    return [{ type, filterValue: value }, ...withoutType];
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * @param {Object}  options
 * @param {string}  options.pageKey             Unique page identifier (used in localStorage key: app:filters:<pageKey>)
 * @param {Object}  [options.filterSchema={}]   Maps URL param names to filter type numbers
 * @param {Array}   [options.defaultFilters=[]] Default filter array
 * @param {Object}  [options.extraParamDefaults={}] Default values for extra URL params
 * @param {number}  [options.defaultPage=0]     Default page index (0-based)
 * @param {number}  [options.defaultPageSize=25] Default rows per page
 * @param {number}  [options.textDebounceMs=300] Debounce delay for text-input URL sync (ms)
 * @param {boolean} [options.persistPageSize=true] Whether to persist page size in URL
 */
function useFilterPersistence({
    pageKey,
    filterSchema = {},
    defaultFilters = [],
    extraParamDefaults = {},
    defaultPage = 0,
    defaultPageSize = 25,
    textDebounceMs = 300,
    persistPageSize = true,
}) {
    // We only use the setter from useSearchParams; the searchParams value itself
    // is only read on first mount (via useState lazy initialisers below) and in
    // the popstate handler. This prevents the component from re-rendering whenever
    // we call setSearchParams.
    const [, setSearchParams] = useSearchParams();

    // Capture stable references to config that shouldn't change between renders.
    // Using refs avoids needing these in useCallback/useEffect dependency arrays.
    const schemaRef = useRef(filterSchema);
    const extraDefaultsRef = useRef(extraParamDefaults);
    const defaultFiltersRef = useRef(defaultFilters);
    const pageKeyRef = useRef(pageKey);
    const defaultPageRef = useRef(defaultPage);
    const defaultPageSizeRef = useRef(defaultPageSize);
    const textDebounceRef = useRef(textDebounceMs);
    const persistPageSizeRef = useRef(persistPageSize);

    // ── Initial state: URL > localStorage > defaults ─────────────────────────

    const [filters, setFiltersState] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const urlFilters = parseFiltersFromParams(params, filterSchema);
        if (urlFilters.length > 0) return urlFilters;

        const stored = loadFilterState(pageKey);
        if (stored?.filters && Array.isArray(stored.filters)) return stored.filters;

        return defaultFilters;
    });

    const [extraParams, setExtraParamsState] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const hasUrlExtras = Object.keys(extraParamDefaults).some(k => params.has('x_' + k));
        if (hasUrlExtras) return parseExtraParamsFromParams(params, extraParamDefaults);

        const stored = loadFilterState(pageKey);
        if (stored?.extraParams && typeof stored.extraParams === 'object') {
            // Only restore keys that are declared in extraParamDefaults.
            // This prevents stale/unknown keys from polluting extraParams (and the URL).
            const knownKeys = Object.keys(extraParamDefaults);
            const filteredExtras = knownKeys.reduce((acc, k) => {
                acc[k] = k in stored.extraParams ? stored.extraParams[k] : extraParamDefaults[k];
                return acc;
            }, {});
            return filteredExtras;
        }
        return { ...extraParamDefaults };
    });

    const [page, setPageState] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const urlPage = parsePageFromParams(params);
        if (urlPage !== null) return urlPage;

        const stored = loadFilterState(pageKey);
        if (typeof stored?.page === 'number' && stored.page >= 0) return stored.page;

        return defaultPage;
    });

    const [pageSize, setPageSizeState] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const urlPageSize = parsePageSizeFromParams(params);
        if (urlPageSize !== null) return urlPageSize;

        const stored = loadFilterState(pageKey);
        if (typeof stored?.pageSize === 'number' && stored.pageSize >= 1) return stored.pageSize;

        return defaultPageSize;
    });

    // ── Refs for latest state (avoids stale closures inside setTimeout) ───────

    const filtersRef = useRef(filters);
    const extraParamsRef = useRef(extraParams);
    const pageRef = useRef(page);
    const pageSizeRef = useRef(pageSize);

    useEffect(() => { filtersRef.current = filters; }, [filters]);
    useEffect(() => { extraParamsRef.current = extraParams; }, [extraParams]);
    useEffect(() => { pageRef.current = page; }, [page]);
    useEffect(() => { pageSizeRef.current = pageSize; }, [pageSize]);

    // Debounce timer
    const syncTimerRef = useRef(null);

    // ── URL + localStorage sync ───────────────────────────────────────────────

    /**
     * Perform sync to URL and localStorage.
     * Always reads from refs so the callback never captures stale state.
     *
     * This is intentionally stored in a ref (not useCallback) so that
     * scheduleSync does not need it in its dependency array, keeping
     * scheduleSync stable and avoiding cascading re-renders.
     */
    const performSyncRef = useRef(null);
    performSyncRef.current = () => {
        const params = buildParams(
            filtersRef.current,
            schemaRef.current,
            extraParamsRef.current,
            extraDefaultsRef.current,
            pageRef.current,
            pageSizeRef.current,
            defaultPageRef.current,
            defaultPageSizeRef.current,
            persistPageSizeRef.current,
        );

        // replaceState: updates the URL without adding a new history entry.
        // This prevents the browser back button from cycling through every
        // keystroke. popstate is NOT fired by replaceState, so there is no
        // risk of a sync loop with our popstate handler below.
        setSearchParams(params, { replace: true });

        saveFilterState(pageKeyRef.current, {
            filters: filtersRef.current,
            extraParams: extraParamsRef.current,
            page: pageRef.current,
            pageSize: pageSizeRef.current,
        });
    };

    /**
     * Schedule a URL + localStorage sync.
     *
     * @param {boolean} debounce  When true, delay by textDebounceMs (for text inputs).
     *                            When false, sync on the next event-loop tick.
     */
    const scheduleSync = useCallback((debounce = false) => {
        clearTimeout(syncTimerRef.current);
        syncTimerRef.current = setTimeout(
            () => performSyncRef.current(),
            debounce ? textDebounceRef.current : 0,
        );
    }, []); // stable — no deps needed because everything is accessed via refs

    // ── Back/forward navigation support ──────────────────────────────────────

    useEffect(() => {
        /**
         * Restore filter state when the user navigates via browser back/forward.
         *
         * Only fires on actual browser navigation (history.back/forward) because
         * replaceState (used for filter updates) does NOT fire popstate.
         * This asymmetry is what prevents sync loops.
         */
        const handlePopState = () => {
            const params = new URLSearchParams(window.location.search);
            const restoredFilters = parseFiltersFromParams(params, schemaRef.current);
            const restoredExtras = parseExtraParamsFromParams(params, extraDefaultsRef.current);
            const restoredPage = parsePageFromParams(params) ?? defaultPageRef.current;
            const restoredPageSize = parsePageSizeFromParams(params) ?? defaultPageSizeRef.current;

            setFiltersState(restoredFilters.length > 0 ? restoredFilters : defaultFiltersRef.current);
            setExtraParamsState(restoredExtras);
            setPageState(restoredPage);
            setPageSizeState(restoredPageSize);
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // If the initial state came from localStorage (URL was empty on load),
    // write it back to the URL so it's immediately shareable/bookmarkable.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const urlFilters = parseFiltersFromParams(params, schemaRef.current);
        const hasUrlExtras = Object.keys(extraDefaultsRef.current).some(k => params.has('x_' + k));
        const hasUrlState = urlFilters.length > 0 || hasUrlExtras || parsePageFromParams(params) !== null;

        if (!hasUrlState && (filters.length > 0 || page > 0)) {
            // Don't push — use replace so the user can still navigate "back" to
            // the pre-filter state cleanly.
            scheduleSync(false);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Public API ────────────────────────────────────────────────────────────

    /**
     * Update a single filter by type.
     * For non-text inputs (dropdowns, toggles) — syncs URL on next tick.
     * Resets pagination to page 0.
     *
     * @param {number} type   - Filter type constant (e.g. GraphFilterTypes.GENERAL)
     * @param {*}      value  - New filter value (empty string removes the filter)
     */
    const setFilter = useCallback((type, value) => {
        setFiltersState(prev => {
            const next = upsertFilter(prev, type, value);
            filtersRef.current = next;
            return next;
        });
        setPageState(0);
        pageRef.current = 0;
        scheduleSync(false);
    }, [scheduleSync]);

    /**
     * Update a single text-input filter with debounced URL sync.
     * Local state updates immediately; URL/localStorage update after debounce.
     * Resets pagination to page 0.
     *
     * @param {number} type   - Filter type constant
     * @param {*}      value  - New filter value
     */
    const setFilterText = useCallback((type, value) => {
        setFiltersState(prev => {
            const next = upsertFilter(prev, type, value);
            filtersRef.current = next;
            return next;
        });
        setPageState(0);
        pageRef.current = 0;
        scheduleSync(true); // debounced
    }, [scheduleSync]);

    /**
     * Replace the entire filter array at once.
     * Useful for initialisation or bulk updates.
     * Resets pagination to page 0.
     *
     * @param {Array} newFilters
     */
    const setFilters = useCallback((newFilters) => {
        setFiltersState(newFilters);
        filtersRef.current = newFilters;
        setPageState(0);
        pageRef.current = 0;
        scheduleSync(false);
    }, [scheduleSync]);

    /**
     * Reset all filters, extra params, and pagination to their defaults.
     * Clears URL params and localStorage.
     */
    const resetFilters = useCallback(() => {
        const defaultExtras = { ...extraDefaultsRef.current };
        setFiltersState(defaultFiltersRef.current);
        setExtraParamsState(defaultExtras);
        setPageState(defaultPageRef.current);
        setPageSizeState(defaultPageSizeRef.current);

        filtersRef.current = defaultFiltersRef.current;
        extraParamsRef.current = defaultExtras;
        pageRef.current = defaultPageRef.current;
        pageSizeRef.current = defaultPageSizeRef.current;

        setSearchParams({}, { replace: true });
        clearFilterState(pageKeyRef.current);
    }, [setSearchParams]);

    /**
     * Get the current value of a specific filter type.
     * Returns '' if the filter is not active (safe for controlled input components).
     *
     * @param  {number} type  - Filter type constant
     * @returns {string}
     */
    const getFilterValue = useCallback((type) => {
        const filter = filters.find(x => x.type === type);
        return filter ? filter.filterValue : '';
    }, [filters]);

    /**
     * Update an extra URL param (e.g. a project-visibility toggle or date range).
     * Does NOT reset pagination.
     *
     * @param {string} key   - Key from extraParamDefaults
     * @param {*}      value - New value
     */
    const setExtraParam = useCallback((key, value) => {
        setExtraParamsState(prev => {
            const next = { ...prev, [key]: value };
            extraParamsRef.current = next;
            return next;
        });
        scheduleSync(false);
    }, [scheduleSync]);

    /**
     * Set the current page. Syncs to URL.
     *
     * @param {number} newPage - 0-based page index
     */
    const setPage = useCallback((newPage) => {
        setPageState(newPage);
        pageRef.current = newPage;
        scheduleSync(false);
    }, [scheduleSync]);

    /**
     * Set the page size. Resets to page 0 and syncs to URL.
     *
     * @param {number} newSize
     */
    const setPageSize = useCallback((newSize) => {
        setPageSizeState(newSize);
        setPageState(0);
        pageSizeRef.current = newSize;
        pageRef.current = 0;
        scheduleSync(false);
    }, [scheduleSync]);

    /**
     * True when any filter is active OR any extra param differs from its default.
     * Use this to conditionally render a "Reset filters" button.
     */
    const hasActiveFilters =
        filters.length > 0 ||
        Object.entries(extraParams).some(([key, val]) =>
            String(val) !== String(extraParamDefaults[key])
        );

    return {
        // Filter array
        filters,
        setFilter,
        setFilterText,
        setFilters,
        resetFilters,
        getFilterValue,
        hasActiveFilters,

        // Extra params (toggles, date ranges, etc.)
        extraParams,
        setExtraParam,

        // Pagination
        page,
        setPage,
        pageSize,
        setPageSize,
    };
}

export default useFilterPersistence;
